/**
 * CustomerFinancialCard — tarjeta compacta de situación financiera del cliente
 * para el panel contextual del Formulario 360 (Fase 4b).
 *
 * Al seleccionar un cliente en el formulario, muestra saldo por cobrar, vencido
 * y crédito disponible + alerta si hay vencidos, reusando el endpoint de Fase 1
 * (GET /customers/<id>/recent-documents/). No interfiere con la lógica del form.
 */
import { useQuery } from '@tanstack/react-query'
import { Loader2, AlertTriangle, Wallet } from 'lucide-react'
import apiClient from '@/lib/apiClient'
import { useClpFormatter } from '@/components/three60'

interface RecentDocsResponse {
  summary: { outstanding_total: string; overdue_total: string; credit_limit: string; credit_available: string | null }
  documents: Array<{ id: string }>
}

export function CustomerFinancialCard({ customerId }: { customerId?: string | null }) {
  const fmt = useClpFormatter()
  const { data, isLoading } = useQuery<RecentDocsResponse>({
    queryKey: ['customer-recent-documents', customerId],
    queryFn: async () => (await apiClient.get(`/sales/customers/${customerId}/recent-documents/`)).data,
    enabled: !!customerId,
  })

  if (!customerId) return null

  return (
    <div className="bg-glass backdrop-blur-glass rounded-card border border-divider shadow-sm overflow-hidden">
      <div className="px-4 py-2.5 border-b border-divider flex items-center gap-2">
        <Wallet size={14} className="text-primary" />
        <span className="text-[10px] uppercase tracking-wider text-content-subtle font-semibold">Situación del cliente</span>
      </div>
      {isLoading || !data ? (
        <div className="grid place-items-center py-6 text-content-muted"><Loader2 className="animate-spin" size={18} /></div>
      ) : (
        <div className="p-4 space-y-2.5">
          <Row label="Saldo por cobrar" value={fmt(data.summary.outstanding_total)} />
          <Row
            label="Vencido"
            value={fmt(data.summary.overdue_total)}
            danger={Number(data.summary.overdue_total) > 0}
          />
          <Row
            label="Crédito disponible"
            value={data.summary.credit_available != null ? fmt(data.summary.credit_available) : 'Sin límite'}
          />
          {Number(data.summary.overdue_total) > 0 && (
            <div className="flex items-center gap-2 bg-warning/15 text-warning rounded-lg px-2.5 py-2 text-[11.5px]">
              <AlertTriangle size={14} /> El cliente tiene saldo vencido.
            </div>
          )}
        </div>
      )}
    </div>
  )
}

function Row({ label, value, danger }: { label: string; value: string; danger?: boolean }) {
  return (
    <div className="flex items-center justify-between">
      <span className="text-xs text-content-muted">{label}</span>
      <span className={`text-sm font-semibold tabular-nums ${danger ? 'text-danger' : 'text-content'}`}>{value}</span>
    </div>
  )
}
